import { cx } from 'class-variance-authority';
import { forwardRef, useEffect, useRef, useState } from 'react';
import BaseImage from '../base-image';

type RotatedImageProps = {
  alt: string;
  src: string;
  rotateIndex: number;
};

const RotatedImage = forwardRef<HTMLDivElement, RotatedImageProps>(({ alt, src, rotateIndex }, ref) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [aspectRatio, setAspectRatio] = useState<'auto' | number>('auto');

  // Because the rotated image is absolutely positioned, a placeholder div is used to maintain the height in the parent
  // Otherwise, if there are multiple groups of failed uploads, they overlap with each other
  // We update the aspect ratio of the placeholder div to be the same as the image once it loads
  // Somewhat suboptimal, because it only considers the size of the 1st image
  useEffect(() => {
    if (rotateIndex === 0) {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        setAspectRatio(aspectRatio);
      };
      img.src = src;
    }
  }, [src, rotateIndex]);

  const getTransformClasses = () => {
    if (rotateIndex === 0) {
      return '';
    }

    const translateClass = rotateIndex % 2 === 0 ? '-translate-x-0.5' : 'translate-x-0.5';

    const pixelMultiple = 3;
    const translateYClass = `translate-y-[${rotateIndex * pixelMultiple}px]`;

    const rotateClass = rotateIndex % 2 === 0 ? '-rotate-2' : 'rotate-2';

    return `${translateClass} ${translateYClass} ${rotateClass}`;
  };

  const getZIndexClass = () => {
    const maxZIndex = 20; // Popover z-index
    return `z-[${maxZIndex - rotateIndex}]`;
  };

  return (
    <>
      <div ref={ref} className={cx('absolute top-0 left-0 w-full', getTransformClasses(), getZIndexClass())}>
        <BaseImage alt={alt} src={src} ref={imageRef} />
      </div>
      {rotateIndex === 0 && <div className="invisible w-full h-auto" style={{ aspectRatio }} />}
    </>
  );
});

export default RotatedImage;
